import { ArrowBack, Email, LocationOn, Phone } from '@mui/icons-material';
import { Box, Container, IconButton, Typography } from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import ContactSection from '../components/ContactSection';
import Footer from '../components/Footer';
import Logo from '../components/Logo';
import { colors } from '../theme';
import './WelcomePage.css';

const ContactPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f5f7fa 0%, #ffffff 100%)',
      }}
    >
      {/* Modern Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${colors.primary.dark} 0%, ${colors.primary.main} 50%, ${colors.primary.light} 100%)`,
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              repeating-linear-gradient(
                45deg,
                transparent,
                transparent 40px,
                ${colors.alpha.white05} 40px,
                ${colors.alpha.white05} 80px
              ),
              repeating-linear-gradient(
                -45deg,
                transparent,
                transparent 40px,
                ${colors.alpha.white05} 40px,
                ${colors.alpha.white05} 80px
              ),
              radial-gradient(
                circle at 20% 20%,
                ${colors.alpha.white10} 0%,
                transparent 50%
              ),
              radial-gradient(
                circle at 80% 80%,
                rgba(255, 214, 79, 0.2) 0%,
                transparent 50%
              )
            `,
            pointerEvents: 'none',
            animation: 'backgroundFloat 20s ease-in-out infinite',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: '-50%',
            left: '-50%',
            width: '200%',
            height: '200%',
            background: `radial-gradient(circle, ${colors.alpha.white05} 2px, transparent 2px)`,
            backgroundSize: '50px 50px',
            pointerEvents: 'none',
            opacity: 0.5,
          },
          '@keyframes backgroundFloat': {
            '0%, 100%': {
              transform: 'translate(0, 0) scale(1)',
              opacity: 1,
            },
            '50%': {
              transform: 'translate(20px, 20px) scale(1.05)',
              opacity: 0.8,
            },
          },
        }}
      >
        {/* Geri Dönüş Butonu */}
        <IconButton
          onClick={() => navigate('/')}
          sx={{
            position: 'absolute',
            top: 20,
            left: 20,
            width: '48px',
            height: '48px',
            background: colors.alpha.white10,
            backdropFilter: 'blur(10px)',
            border: `1px solid ${colors.alpha.white10}`,
            color: 'white',
            zIndex: 10,
            '&:hover': {
              background: colors.alpha.white30,
              transform: 'scale(1.05)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          <ArrowBack />
        </IconButton>

        <Container maxWidth="lg">
          <Box
            sx={{
              py: { xs: 2.5, md: 3 },
              textAlign: 'center',
              position: 'relative',
            }}
          >
            {/* Logo ve Başlık */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                animation: 'fadeInDown 0.6s ease-out',
                '@keyframes fadeInDown': {
                  from: {
                    opacity: 0,
                    transform: 'translateY(-20px)',
                  },
                  to: {
                    opacity: 1,
                    transform: 'translateY(0)',
                  },
                },
              }}
            >
              <Logo size={48} animate />
              <Typography
                variant="h4"
                fontWeight={700}
                sx={{
                  fontSize: { xs: '1.5rem', md: '2rem' },
                  textShadow: '0 2px 10px rgba(0,0,0,0.1)',
                }}
              >
                Dietaro
              </Typography>
            </Box>

            {/* Hızlı İletişim Kartları */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                gap: 1.5,
                mt: 0,
                maxWidth: '750px',
                mx: 'auto',
              }}
            >
              {/* Telefon Kartı */}
              <Box
                sx={{
                  background: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 2.5,
                  p: 2.5,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  animation: 'fadeInScale 0.6s ease-out 0.3s backwards',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.3)',
                    transform: 'translateY(-8px) scale(1.03)',
                    boxShadow: '0 12px 24px rgba(0, 0, 0, 0.15)',
                    borderColor: 'rgba(255, 255, 255, 0.4)',
                  },
                  '&:hover .icon-box': {
                    transform: 'rotate(12deg) scale(1.1)',
                  },
                  '@keyframes fadeInScale': {
                    from: {
                      opacity: 0,
                      transform: 'scale(0.8)',
                    },
                    to: {
                      opacity: 1,
                      transform: 'scale(1)',
                    },
                  },
                }}
                onClick={() => window.open('tel:+904624100410')}
              >
                <Box
                  className="icon-box"
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.25)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 1,
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <Phone sx={{ fontSize: 22 }} />
                </Box>
                <Typography
                  variant="body2"
                  fontWeight={700}
                  gutterBottom
                  sx={{ fontSize: '0.9rem' }}
                >
                  Telefon
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  +90 (462) 410 04 10
                </Typography>
              </Box>

              {/* Email Kartı */}
              <Box
                sx={{
                  background: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 2.5,
                  p: 2.5,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  animation: 'fadeInScale 0.6s ease-out 0.4s backwards',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.3)',
                    transform: 'translateY(-8px) scale(1.03)',
                    boxShadow: '0 12px 24px rgba(0, 0, 0, 0.15)',
                    borderColor: 'rgba(255, 255, 255, 0.4)',
                  },
                  '&:hover .icon-box': {
                    transform: 'rotate(-12deg) scale(1.1)',
                  },
                  '@keyframes fadeInScale': {
                    from: {
                      opacity: 0,
                      transform: 'scale(0.8)',
                    },
                    to: {
                      opacity: 1,
                      transform: 'scale(1)',
                    },
                  },
                }}
                onClick={() => window.open('mailto:omek@email.com')}
              >
                <Box
                  className="icon-box"
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.25)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 1,
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <Email sx={{ fontSize: 22 }} />
                </Box>
                <Typography
                  variant="body2"
                  fontWeight={700}
                  gutterBottom
                  sx={{ fontSize: '0.9rem' }}
                >
                  E-posta
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  omek@email.com
                </Typography>
              </Box>

              {/* Adres Kartı */}
              <Box
                sx={{
                  background: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 2.5,
                  p: 2.5,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  animation: 'fadeInScale 0.6s ease-out 0.5s backwards',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.3)',
                    transform: 'translateY(-8px) scale(1.03)',
                    boxShadow: '0 12px 24px rgba(0, 0, 0, 0.15)',
                    borderColor: 'rgba(255, 255, 255, 0.4)',
                  },
                  '&:hover .icon-box': {
                    transform: 'scale(1.1)',
                    animation: 'bounce 0.6s ease-in-out',
                  },
                  '@keyframes fadeInScale': {
                    from: {
                      opacity: 0,
                      transform: 'scale(0.8)',
                    },
                    to: {
                      opacity: 1,
                      transform: 'scale(1)',
                    },
                  },
                  '@keyframes bounce': {
                    '0%, 100%': {
                      transform: 'scale(1.1) translateY(0)',
                    },
                    '50%': {
                      transform: 'scale(1.1) translateY(-5px)',
                    },
                  },
                }}
                onClick={() =>
                  window.open(
                    'https://www.google.com/maps?q=40.6551,29.2767+(Yalova+İş+Merkezi)&z=17',
                    '_blank'
                  )
                }
              >
                <Box
                  className="icon-box"
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.25)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 1,
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <LocationOn sx={{ fontSize: 22 }} />
                </Box>
                <Typography
                  variant="body2"
                  fontWeight={700}
                  gutterBottom
                  sx={{ fontSize: '0.9rem' }}
                >
                  Adres
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  Yalova İş Merkezi
                </Typography>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Contact Section */}
      <ContactSection />

      {/* Footer */}
      <Footer />
    </Box>
  );
};

export default ContactPage;
